import {BehaviorSubject, Observable, tap} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {IPlan, IPriceInfo, ISession} from './payment.models';

import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {InterceptorSkipHeader} from '../../interceptors/token.interceptor';
import {environment} from '../../../../environments/environment';
import {isPlatformServer} from '@angular/common';

@Injectable({providedIn: 'root'})
export class PaymentService {
  private httpClient = inject(HttpClient);
  private platform = inject(PLATFORM_ID);

  private users = new BehaviorSubject<number>(5);
  private priceInfo = new BehaviorSubject<IPriceInfo | null>(null);

  getPrice(users: number): Observable<IPriceInfo> {
    return this.httpClient
      .post<IPriceInfo>(
        `${environment.apiUrl}/companies/price`,
        {
          subscriptions_count: users,
        },
        {
          headers: new HttpHeaders({[InterceptorSkipHeader]: 'true'}),
        }
      )
      .pipe(tap((info) => this.setPriceInfo(info)));
  }

  getUsers$(): Observable<number> {
    return this.users.asObservable();
  }

  getUsers(): number {
    return this.users.getValue();
  }

  setUsers(users: number): void {
    this.users.next(users);
  }

  getPriceInfo(): IPriceInfo | null {
    return this.priceInfo.getValue();
  }

  getPriceInfo$(): Observable<IPriceInfo | null> {
    return this.priceInfo.asObservable();
  }

  setPriceInfo(info: IPriceInfo): void {
    this.priceInfo.next(info);
  }

  session(): Observable<ISession> {
    return this.httpClient
      .post<ISession>(`${environment.apiUrl}/companies/session`, {
        subscriptions_count: this.getUsers(),
        price_id: this.getPriceInfo()?.price_id,
      })
      .pipe(
        tap((data) => {
          if (data?.url) {
            window.location.replace(data.url);
          }
        })
      );
  }

  getPlan(): Observable<IPlan> {
    return this.httpClient.get<IPlan>(
      `${environment.apiUrl}/companies/current/plan`
    );
  }

  setLocalPriceInfo(info: IPriceInfo): void {
    localStorage.setItem('priceInfo', JSON.stringify(info));
  }

  getLocalPriceInfo(): IPriceInfo | null {
    if (isPlatformServer(this.platform)) {
      return null;
    }

    const priceInfo = localStorage.getItem('priceInfo');

    if (priceInfo) {
      return JSON.parse(priceInfo);
    }

    return null;
  }

  removeLocalPriceInfo(): void {
    localStorage.removeItem('priceInfo');
  }

  setLocalUsers(users: number): void {
    localStorage.setItem('users', users.toString());
  }

  getLocalUsers(): number | null {
    if (isPlatformServer(this.platform)) {
      return null;
    }

    const users = localStorage.getItem('users');

    if (users) {
      return +users;
    }

    return null;
  }

  removeLocalUsers(): void {
    localStorage.removeItem('users');
  }
}
